import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { setCircularLoading } from "../../reducers/UiReducer";
import { getService } from "../../reducers/service";
import { notify } from "../../reducers/NotifierReducer";
import { serviceNames } from "../../utils/constants";
import { PATH_APPLICANTS } from "../../utils/routeConstants";
import { mapDictionaryOptions } from "../../utils/functions";

export const useApplicantData = (id, reset) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userAgency = useSelector((state) => state.user.userAgency);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const [yearAppliedOptions, setYearAppliedOptions] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const statusOptions = mapDictionaryOptions(dictionary.codePerApplicantStatus);
  const applicantTestResultOptions = mapDictionaryOptions(
    dictionary.codePerApplicantTestResult
  );
  const raceOptions = mapDictionaryOptions(dictionary.codeRace);
  const genderOptions = mapDictionaryOptions(dictionary.codeSex);
  const ethnicityOptions = mapDictionaryOptions(dictionary.codeEthnicity);
  const tagTypeOptions = mapDictionaryOptions(dictionary.codePerTagTypes);
  const suffixOptions = mapDictionaryOptions(dictionary.codeNameSuffixes);
  const maritalOptions = mapDictionaryOptions(dictionary.codeMaritalStatus);
  const educationOptions = mapDictionaryOptions(dictionary.codePerEducation);
  const stateOptions = mapDictionaryOptions(dictionary.codeStates);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        dispatch(setCircularLoading(true));
        setLoaded(false);
        const services = {
          yearOptions: getService(serviceNames.perApplicantYearOptions),
        };

        const results = await Promise.all([
          services.yearOptions.find({
            query: {
              agencyId: userAgency,
            },
          }),
        ]);
        if (isMounted) {
          if (Array.isArray(results[0])) {
            setYearAppliedOptions(
              results[0].map((value) => ({
                label: value,
                value,
              }))
            );
          }
        }

        if (id) {
          const service = getService(serviceNames.perApplicant);

          const result = await service.get(id);
          if (isMounted && result) {
            reset({
              id: result.id || null,
              suffix: result.suffix || "",
              lastName: result.lastName || "",
              firstName: result.firstName || "",
              middleName: result.middleName || "",
              dob: result?.dob ? moment(result.dob).startOf("day").format("MM/DD/YYYY") : "",
              ssn: result.ssn || "",
              ethnicity: result.ethnicity || "",
              gender: result.gender || "",
              race: result.race || "",
              maritalStatus: result.maritalStatus || "",
              phone: result.phone || "",
              email: result.email || "",
              workPhoneNum: result.workPhoneNum || "",
              priorLawEnforcement: result.priorLawEnforcement || false,
              prepSchool: result.prepSchool || false,
              address: result.address || "",
              city: result.city || "",
              state: result.state || "",
              zip: result.zip || "",
              comments: result.comments || "",
              militaryRelatedNotes: result.militaryRelatedNotes || "",
              education: result.education || "",
              pat: result.pat || "",
              patDate: result.patDate
                ? moment(result.patDate).format("MM/DD/YYYY")
                : "",
              patNotes: result.patNotes || null,
              written: result.written || "",
              writtenDate: result.writtenDate
                ? moment(result.writtenDate).format("MM/DD/YYYY")
                : "",
              writtenNotes: result.writtenNotes || null,
              polygraph: result.polygraph || "",
              polygraphDate: result.polygraphDate
                ? moment(result.polygraphDate).format("MM/DD/YYYY")
                : "",
              polygraphNotes: result.polygraphNotes || null,
              background: result.background || "",
              backgroundDateSent: result.backgroundDateSent
                ? moment(result.backgroundDateSent).format("MM/DD/YYYY")
                : "",
              backgroundDateReturned: result.backgroundDateReturned
                ? moment(result.backgroundDateReturned).format("MM/DD/YYYY")
                : "",
              backgroundNotes: result.backgroundNotes || null,
              backgroundInvestigator: result.backgroundInvestigator || null,
              oralCommittee: result.oralCommittee || "",
              oralCommitteeDate: result.oralCommitteeDate
                ? moment(result.oralCommitteeDate).format("MM/DD/YYYY")
                : "",
              oralCommitteeNotes: result.oralCommitteeNotes || null,
              oralDirector: result.oralDirector || "",
              oralDirectorDate: result.oralDirectorDate
                ? moment(result.oralDirectorDate).format("MM/DD/YYYY")
                : "",
              oralDirectorNotes: result.oralDirectorNotes || null,
              eligibility: result.eligibility || false,
              medical: result.medical || "",
              medicalDate: result.medicalDate
                ? moment(result.medicalDate).format("MM/DD/YYYY")
                : "",
              medicalNotes: result.medicalNotes || "",
              psych: result.psych || "",
              psychDate: result.psychDate
                ? moment(result.psychDate).format("MM/DD/YYYY")
                : "",
              psychNotes: result.psychNotes || null,
              boxNum: result.boxNum || "",
              yearApplied: result.yearApplied || "",
              status: result.status || "",
              testNotes: result.testNotes || "",
            });
          }
        }
      } catch (error) {
        console.error(error);
        if (isMounted) {
          dispatch(notify("Failed to fetch applicant", "error"));
          history.push(PATH_APPLICANTS);
        }
      } finally {
        dispatch(setCircularLoading(false));
        setLoaded(true);
      }
    };

    init();
    return () => {
      isMounted = false;
    };
  }, [id, dispatch, reset, userAgency]);
  return {
    genderOptions,
    ethnicityOptions,
    maritalOptions,
    stateOptions,
    statusOptions,
    applicantTestResultOptions,
    educationOptions,
    yearAppliedOptions,
    raceOptions,
    tagTypeOptions,
    suffixOptions,
    loaded,
  };
};
